/**
 * Rotas e o portão de autenticação.
 *
 * O redirecionamento entre área logada e deslogada acontece só aqui. Espalhar
 * isso pelas páginas leva a piscadas e a rotas alcançáveis por URL direta sem
 * sessão.
 */

import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { ErrorBoundary } from '@/components/error-boundary';
import { AppLayout } from '@/components/layout';
import { postAuthPath } from '@/lib/pending-invite';
import { useAuth } from '@/store/auth';

import { LoginPage } from '@/pages/login';
import { RegisterPage } from '@/pages/register';
import { ForgotPasswordPage, ResetPasswordPage } from '@/pages/password-reset';
import { DashboardPage } from '@/pages/dashboard';
import { GroupsPage } from '@/pages/groups';
import { GroupDetailPage } from '@/pages/group-detail';
import { GroupEditPage } from '@/pages/group-edit';
import { GameDetailPage } from '@/pages/game-detail';
import { TeamsPage } from '@/pages/teams';
import { RatePage } from '@/pages/rate';
import { ProfilePage } from '@/pages/profile';
import { NotificationsPage } from '@/pages/notifications';
import { InvitePage } from '@/pages/invite';
import { NotFoundPage } from '@/pages/not-found';
import { PlatformBillingPage } from '@/pages/platform-billing';

export function App() {
  const { isAuthenticated, initializing, bootstrap } = useAuth();
  const isPlatformAdmin = useAuth((state) => state.user?.isPlatformAdmin ?? false);
  const location = useLocation();

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  if (initializing) {
    return <BootSplash />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/entrar" element={<LoginPage />} />
        <Route path="/criar-conta" element={<RegisterPage />} />
        <Route path="/esqueci-a-senha" element={<ForgotPasswordPage />} />
        {/* Destino do link do e-mail — precisa casar com MailService.sendPasswordReset. */}
        <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
        {/* Convite é público: quem recebe o link pode nem ter conta ainda. */}
        <Route path="/convite/:code" element={<InvitePage />} />
        <Route
          path="*"
          element={<Navigate to="/entrar" replace state={{ from: location.pathname }} />}
        />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Já autenticado: não faz sentido ver login. Quem veio de um convite volta
          para ele, que conclui a entrada no grupo. */}
      <Route path="/entrar" element={<Navigate to={postAuthPath('/')} replace />} />
      <Route path="/criar-conta" element={<Navigate to={postAuthPath('/')} replace />} />
      <Route path="/esqueci-a-senha" element={<Navigate to="/" replace />} />
      {/* O link do e-mail pode abrir num navegador já logado (outra conta, inclusive). */}
      <Route path="/redefinir-senha" element={<ResetPasswordPage />} />

      <Route
        element={
          <ErrorBoundary>
            <AppLayout />
          </ErrorBoundary>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/grupos" element={<GroupsPage />} />
        <Route path="/grupos/:groupId" element={<GroupDetailPage />} />
        <Route path="/grupos/:groupId/editar" element={<GroupEditPage />} />
        <Route path="/partidas/:gameId" element={<GameDetailPage />} />
        <Route path="/partidas/:gameId/times" element={<TeamsPage />} />
        <Route path="/partidas/:gameId/avaliar" element={<RatePage />} />
        {/* O financeiro agora é uma aba dentro de cada pelada. */}
        <Route path="/financeiro" element={<Navigate to="/grupos" replace />} />
        <Route path="/perfil" element={<ProfilePage />} />
        <Route path="/notificacoes" element={<NotificationsPage />} />
        <Route path="/convite/:code" element={<InvitePage />} />
        {/* Esconder a rota é conveniência; quem barra de fato é a API (404). */}
        {isPlatformAdmin ? (
          <Route path="/admin/assinaturas" element={<PlatformBillingPage />} />
        ) : null}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

function BootSplash() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-bg">
      <div className="flex flex-col items-center gap-3">
        <span className="flex h-14 w-14 animate-shimmer items-center justify-center rounded-2xl bg-brand">
          <svg
            viewBox="0 0 24 24"
            className="h-8 w-8 text-on-brand"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a10 10 0 0 0 0 20M12 2a10 10 0 0 1 0 20M2 12h20" />
          </svg>
        </span>
        <span className="text-sm font-semibold text-ink-3">Carregando…</span>
      </div>
    </div>
  );
}
